import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { Modal } from '../ui/Modal';
import { Input } from '../ui/Input';
import { Button } from '../ui/Button';
import { Badge } from '../ui/Badge';
import { useAdminBroadcast } from '../../hooks/useAdmin';
import { Megaphone, AlertTriangle, ShieldAlert, Link as LinkIcon, Server, Bell } from 'lucide-react';
import { cn } from '../../lib/utils';

const schema = z.object({
  nodeId: z.string().min(1, 'Target Org ID is required'),
  type: z.enum(['ANNOUNCEMENT', 'ALERT']),
  title: z.string().min(1, 'Broadcast title is required').max(200, 'Title too long'),
  message: z.string().min(1, 'Broadcast message is required'),
  link: z.string().optional().or(z.literal('')),
});

type FormData = z.infer<typeof schema>;

interface AdminBroadcastModalProps {
  isOpen: boolean;
  onClose: () => void;
  defaultNodeId?: string;
  defaultNodeName?: string;
}

export const AdminBroadcastModal: React.FC<AdminBroadcastModalProps> = ({
  isOpen,
  onClose,
  defaultNodeId = 'platform-root',
  defaultNodeName
}) => {
  const broadcastMutation = useAdminBroadcast();

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    reset,
    formState: { errors }
  } = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: {
      nodeId: defaultNodeId,
      type: 'ANNOUNCEMENT',
      title: '',
      message: '',
      link: ''
    }
  });

  useEffect(() => {
    if (isOpen) {
      setValue('nodeId', defaultNodeId || 'platform-root');
    }
  }, [isOpen, defaultNodeId, setValue]);

  const broadcastType = watch('type');

  const onSubmit = (data: FormData) => {
    broadcastMutation.mutate(
      {
        nodeId: data.nodeId.trim(),
        params: {
          type: data.type,
          title: data.title.trim(),
          message: data.message.trim(),
          link: data.link?.trim() || undefined
        }
      },
      {
        onSuccess: () => {
          reset();
          onClose();
        }
      }
    );
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Admin Global Broadcast"
      className="max-w-xl border-4 border-black dark:border-white rounded-none bg-white dark:bg-slate-950 p-0 overflow-hidden"
      footer={
        <div className="flex justify-between items-center w-full">
          <div className="flex items-center gap-2">
            <Badge variant={broadcastType === 'ALERT' ? 'red' : 'blue'} className="uppercase font-mono text-[10px] tracking-widest">
              {broadcastType === 'ALERT' ? 'URGENT ALERT' : 'GLOBAL ANNOUNCEMENT'}
            </Badge>
          </div>
          <div className="flex gap-3">
            <Button variant="secondary" onClick={onClose} className="rounded-none border-2 border-black dark:border-white">
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleSubmit(onSubmit)}
              isLoading={broadcastMutation.isPending}
              className={cn(
                "px-6 rounded-none font-bold uppercase tracking-wider border-2 border-black dark:border-white text-white",
                broadcastType === 'ALERT' 
                  ? "bg-red-600 hover:bg-red-700" 
                  : "bg-black text-white hover:bg-[#0044FF] dark:bg-white dark:text-black dark:hover:bg-[#0044FF] dark:hover:text-white"
              )}
            >
              <Megaphone size={16} className="mr-1.5" /> Send Broadcast
            </Button>
          </div>
        </div>
      }
    >
      <div className="space-y-6 pt-2">
        <div className={cn(
          "p-4 border-2 rounded-none flex items-start gap-3",
          broadcastType === 'ALERT'
            ? "bg-red-50 dark:bg-red-950/30 border-red-500"
            : "bg-blue-50 dark:bg-blue-950/30 border-black dark:border-white"
        )}>
          {broadcastType === 'ALERT' ? (
            <AlertTriangle className="text-red-600 flex-shrink-0 mt-0.5" size={20} />
          ) : (
            <Megaphone className="text-[#0044FF] flex-shrink-0 mt-0.5" size={20} />
          )}
          <div className="text-xs space-y-1">
            <div className="font-bold uppercase tracking-wider text-gray-900 dark:text-white">
              {broadcastType === 'ALERT' ? 'High-Priority Alert Dispatch' : 'Platform Announcement Dispatch'}
            </div>
            <p className="leading-relaxed text-gray-600 dark:text-gray-300">
              {broadcastType === 'ALERT'
                ? 'Dispatches an urgent alert notification across members of the target org hierarchy.'
                : 'Dispatches a general announcement or update to all members of the target org hierarchy.'}
            </p>
          </div>
        </div>

        <div className="space-y-4">
          {/* Broadcast Type Selector */}
          <div>
            <label className="block text-xs font-black uppercase tracking-wider text-gray-700 dark:text-gray-300 mb-2">
              Broadcast Type
            </label>
            <div className="grid grid-cols-2 gap-3">
              <label
                className={cn(
                  "flex items-center gap-2.5 p-3 border-2 cursor-pointer transition-colors text-xs font-bold",
                  broadcastType === 'ANNOUNCEMENT'
                    ? "border-black dark:border-white bg-blue-50 dark:bg-blue-950/40 text-blue-900 dark:text-blue-200"
                    : "border-gray-200 dark:border-gray-800 hover:border-gray-400 text-gray-600 dark:text-gray-400"
                )}
              >
                <input
                  type="radio"
                  value="ANNOUNCEMENT"
                  {...register('type')}
                  className="sr-only"
                />
                <Megaphone size={16} className={broadcastType === 'ANNOUNCEMENT' ? "text-[#0044FF]" : "text-gray-400"} />
                <div>
                  <div className="font-black uppercase tracking-wider">Announcement</div>
                  <div className="text-[10px] font-normal text-gray-500 dark:text-gray-400">Normal notification / update</div>
                </div>
              </label>

              <label
                className={cn(
                  "flex items-center gap-2.5 p-3 border-2 cursor-pointer transition-colors text-xs font-bold",
                  broadcastType === 'ALERT'
                    ? "border-red-600 bg-red-50 dark:bg-red-950/40 text-red-900 dark:text-red-200"
                    : "border-gray-200 dark:border-gray-800 hover:border-gray-400 text-gray-600 dark:text-gray-400"
                )}
              >
                <input
                  type="radio"
                  value="ALERT"
                  {...register('type')}
                  className="sr-only"
                />
                <AlertTriangle size={16} className={broadcastType === 'ALERT' ? "text-red-600" : "text-gray-400"} />
                <div>
                  <div className="font-black uppercase tracking-wider">Emergency Alert</div>
                  <div className="text-[10px] font-normal text-gray-500 dark:text-gray-400">Urgent high-priority alert</div>
                </div>
              </label>
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="block text-xs font-black uppercase tracking-wider text-gray-700 dark:text-gray-300">
                Target Org ID
              </label>
              {defaultNodeName && (
                <span className="text-[11px] font-mono text-blue-600 dark:text-blue-400 font-bold">
                  {defaultNodeName}
                </span>
              )}
            </div>
            <div className="relative">
              <Server className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
              <input
                type="text"
                placeholder="e.g. platform-root or org-id"
                {...register('nodeId')}
                className={cn(
                  "w-full pl-9 pr-3 py-2.5 text-sm font-mono border-2 border-black dark:border-white rounded-none bg-white dark:bg-slate-900 focus:ring-2 focus:ring-blue-500 outline-none",
                  errors.nodeId && "border-red-500"
                )}
              />
            </div>
            {errors.nodeId && <p className="text-xs text-red-500 mt-1">{errors.nodeId.message}</p>}
            <p className="text-[10px] text-gray-400 font-mono mt-1">Use <code className="font-bold">platform-root</code> for system-wide platform blast.</p>
          </div>

          <div>
            <Input
              label="Broadcast Title"
              placeholder="e.g. Scheduled Maintenance or New Features Available"
              {...register('title')}
              errorMessage={errors.title?.message}
              className="rounded-none border-2 border-black dark:border-white font-bold"
            />
          </div>

          <div>
            <label className="block text-xs font-black uppercase tracking-wider text-gray-700 dark:text-gray-300 mb-1">
              Notification Message
            </label>
            <textarea
              rows={4}
              placeholder="Provide clear announcement details or status instructions for members..."
              {...register('message')}
              className={cn(
                "w-full px-3 py-2 text-sm border-2 border-black dark:border-white rounded-none bg-white dark:bg-slate-900 focus:ring-2 focus:ring-blue-500 outline-none dark:text-white",
                errors.message && "border-red-500"
              )}
            />
            {errors.message && <p className="text-xs text-red-500 mt-1">{errors.message.message}</p>}
          </div>

          <div>
            <div className="flex items-center gap-1.5 mb-1">
              <LinkIcon size={14} className="text-gray-500" />
              <label className="block text-xs font-black uppercase tracking-wider text-gray-700 dark:text-gray-300">
                Action URL / Redirect Link (Optional)
              </label>
            </div>
            <input
              type="text"
              placeholder="e.g. /billing or /settings"
              {...register('link')}
              className="w-full px-3 py-2 text-sm font-mono border-2 border-black dark:border-white rounded-none bg-white dark:bg-slate-900 focus:ring-2 focus:ring-blue-500 outline-none"
            />
          </div>
        </div>
      </div>
    </Modal>
  );
};
